import React, { useEffect, useRef, useState } from "react";
import { CircleMarker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { MagnifyingGlass, X, MapPin } from "@phosphor-icons/react";
import { geocode } from "../../lib/proximity";
import { zoneShortName, EVAC_LEVEL_META } from "./SheriffEvacLayer";

// "Level 3 — Old Trails Zone 2551 (Spokane County)" -> "2551"
// "A1330 — Okanogan County, Washington" -> "A1330"
function zoneCode(a) {
  const m = (a.title || "").match(/\bzone\s+([A-Za-z]?\d+[A-Za-z]?)\b/i);
  if (m) return m[1].toUpperCase();
  const short = zoneShortName(a.title);
  return /^[A-Za-z]{0,2}\d{2,}$/.test(short) ? short.toUpperCase() : null;
}

const ZONE_CODE_RE = /^[A-Za-z]{0,2}\d{2,}[A-Za-z]?$/;

export function matchZones(q, alerts) {
  const qn = (q || "").trim().toUpperCase();
  if (qn.length < 2) return [];
  const out = [];
  for (const a of alerts) {
    const code = zoneCode(a);
    const short = zoneShortName(a.title || "").toUpperCase();
    if (
      (code && (code === qn || (qn.length >= 3 && code.startsWith(qn)))) ||
      (qn.length >= 3 && short.includes(qn))
    ) {
      out.push(a);
    }
  }
  return out.sort((x, y) => (y.level || 0) - (x.level || 0)).slice(0, 5);
}

export function CaptureMap({ onMap }) {
  const map = useMap();
  useEffect(() => {
    onMap(map);
  }, [map, onMap]);
  return null;
}

export function SearchMarker({ marker }) {
  if (!marker) return null;
  // Prefer the user's typed input as the visible label so their search term
  // "sticks" to the location on the map. Fall back to the geocoder's full
  // display name if we don't have the raw query for some reason.
  const label = marker.user_query || marker.display_name;
  return (
    <CircleMarker
      center={[marker.lat, marker.lon]}
      radius={9}
      pathOptions={{ color: "#4CC9F0", weight: 2.5, fillColor: "#4CC9F0", fillOpacity: 0.35 }}
    >
      <Tooltip
        direction="top"
        offset={[0, -8]}
        permanent
        opacity={0.95}
        className="search-marker-label"
      >
        <span className="font-bold">{label}</span>
        {marker.user_query && marker.display_name && marker.display_name !== marker.user_query && (
          <span className="ml-1 opacity-70 text-[10px]">
            · {marker.display_name.split(",").slice(0, 2).join(",")}
          </span>
        )}
      </Tooltip>
    </CircleMarker>
  );
}

export function MapSearchBox({ map, onMarker, onSelect, zoomTo = 12, startOpen = false, evacAlerts = [], onZone }) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(startOpen);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [zones, setZones] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && !startOpen && inputRef.current) inputRef.current.focus();
  }, [open, startOpen]);

  const pick = (r) => {
    // Carry the raw user-typed input along with the geocoded result so the
    // map pin shows what the user searched for (e.g. "Loomis") instead of
    // the geocoder's long display name.
    const enriched = { ...r, user_query: query.trim() || r.display_name };
    setResults([]);
    setZones([]);
    setError(null);
    setQuery(enriched.user_query);
    if (map) map.flyTo([enriched.lat, enriched.lon], Math.max(map.getZoom(), zoomTo), { duration: 0.8 });
    onMarker?.(enriched);
    onSelect?.(enriched);
  };

  const pickZone = (a) => {
    setZones([]);
    setResults([]);
    setError(null);
    // SheriffEvacLayer's focusEvac effect handles the fly-to + popup open.
    onZone?.(a);
  };

  const runSearch = async () => {
    const q = query.trim();
    if (!q || searching) return;
    setSearching(true);
    setError(null);
    setResults([]);
    const zm = matchZones(q, evacAlerts);
    setZones(zm);
    if (zm.length > 0 && ZONE_CODE_RE.test(q)) {
      // Pure zone-code query with matches: no geocoding needed.
      if (zm.length === 1) pickZone(zm[0]);
      setSearching(false);
      return;
    }
    try {
      const res = await geocode(q);
      if ((!res || res.length === 0) && zm.length === 0) setError("No matches found");
      else if (res && res.length === 1 && zm.length === 0) pick(res[0]);
      else setResults((res || []).slice(0, 6));
    } catch {
      if (zm.length === 0) setError("Search failed — try again");
    } finally {
      setSearching(false);
    }
  };

  const collapse = () => {
    setOpen(false);
    setResults([]);
    setZones([]);
    setError(null);
    setQuery("");
    onMarker?.(null);
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="map-search-toggle"
        title="Search address or town"
        onClick={() => setOpen(true)}
        className="px-2.5 py-1.5 rounded-sm border bg-tac-bg/85 backdrop-blur-xl border-tac-border text-tac-text hover:border-tac-borderBright transition-colors"
      >
        <MagnifyingGlass size={14} weight="bold" />
      </button>
    );
  }

  return (
    <div className="relative w-64 sm:w-72" data-testid="map-search-control">
      <div className="flex items-center gap-1.5 bg-tac-bg/90 backdrop-blur-xl border border-tac-border rounded-sm p-1">
        <div className="relative flex-1">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-tac-faint" />
          <input
            ref={inputRef}
            data-testid="map-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                runSearch();
              }
            }}
            placeholder={evacAlerts.length ? "Address, town, or zone #…" : "Address + town…"}
            className="w-full bg-tac-surface border border-tac-border focus:border-tac-primary rounded-sm pl-7 pr-2 py-1 text-[13px] text-tac-text placeholder:text-tac-faint outline-none"
          />
        </div>
        <button
          data-testid="map-search-submit"
          type="button"
          onClick={runSearch}
          disabled={searching || !query.trim()}
          className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-tac-primary text-white rounded-sm hover:bg-tac-primary/90 disabled:opacity-40"
        >
          {searching ? "…" : "Go"}
        </button>
        {!startOpen && (
          <button
            type="button"
            data-testid="map-search-close"
            onClick={collapse}
            className="px-1 text-tac-dim hover:text-tac-text"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>
      {(error || results.length > 0 || zones.length > 0) && (
        <div className="absolute left-0 top-full mt-1 w-full z-[1001]">
          {zones.length > 0 && (
            <div
              data-testid="zone-search-results"
              className="border border-tac-border bg-tac-bg/95 backdrop-blur-xl rounded-sm divide-y divide-tac-border/60 mb-1"
            >
              {zones.map((a) => {
                const meta = EVAC_LEVEL_META[a.level] || EVAC_LEVEL_META[1];
                return (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`zone-search-result-${a.id}`}
                    onClick={() => pickZone(a)}
                    className="w-full text-left px-2 py-1.5 hover:bg-tac-surface2 flex items-start gap-1.5"
                  >
                    <span
                      className="shrink-0 mt-0.5 px-1.5 py-0.5 rounded-sm text-[9px] font-bold text-white leading-none"
                      style={{ background: meta.color }}
                    >
                      {meta.label}
                    </span>
                    <span className="text-[12px] text-tac-text leading-snug">{a.title}</span>
                  </button>
                );
              })}
            </div>
          )}
          {error && (
            <div data-testid="map-search-error" className="bg-tac-bg/90 backdrop-blur-xl border border-tac-border rounded-sm px-2 py-1.5 text-[12px] text-tac-danger">
              {error}
            </div>
          )}
          {results.length > 0 && (
            <div
              data-testid="map-search-results"
              className="border border-tac-border bg-tac-bg/95 backdrop-blur-xl rounded-sm divide-y divide-tac-border/60 max-h-44 overflow-y-auto tac-scroll"
            >
              {results.map((r, idx) => (
                <button
                  key={`${r.lat}-${r.lon}-${idx}`}
                  type="button"
                  data-testid={`map-search-result-${idx}`}
                  onClick={() => pick(r)}
                  className="w-full text-left px-2 py-1.5 hover:bg-tac-surface2 flex items-start gap-1.5"
                >
                  <MapPin size={11} weight="fill" className="text-tac-primary shrink-0 mt-0.5" />
                  <span className="text-[12px] text-tac-text leading-snug">{r.display_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MapSearchControl({
  onSelect,
  showMarker = true,
  startOpen = false,
  zoomTo = 12,
  position = "top-right",
  className = null,
}) {
  const map = useMap();
  const boxRef = useRef(null);
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    if (boxRef.current) {
      L.DomEvent.disableClickPropagation(boxRef.current);
      L.DomEvent.disableScrollPropagation(boxRef.current);
    }
  });

  const posCls = className || (position === "top-left" ? "top-3 left-3" : "top-3 right-3");

  return (
    <>
      <div ref={boxRef} className={`absolute ${posCls} z-[1000]`}>
        <MapSearchBox
          map={map}
          onMarker={showMarker ? setMarker : undefined}
          onSelect={onSelect}
          startOpen={startOpen}
          zoomTo={zoomTo}
        />
      </div>
      <SearchMarker marker={marker} />
    </>
  );
}
